import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import * as React from 'react'
import { ReactNode, MouseEventHandler } from 'react'
import { FilledInButton, OutlinedButton } from 'shared/ui'

export type ConfirmDialogProps = {
  title: string
  open?: boolean
  onClose: MouseEventHandler
  onConfirm: MouseEventHandler
  cta?: string
  cancel?: string
  canConfirm?: boolean
  children?: ReactNode
  destructive?: boolean
}

/**
 * ConfirmDialog is a reusable dialog component that can be used to display
 * confirmation dialogs with customizable content and actions.
 *
 * @param {string} title - The title of the dialog.
 * @param {ReactNode} children - Content for the dialog
 * @param {boolean} open - A boolean indicating whether the dialog is open.
 * @param {() => void} onClose - A callback function to be called when the dialog is closed.
 * @param {() => void} onConfirm - A callback function to be called when the confirm button is clicked.
 * @param {boolean} [shouldHide=false] - A boolean indicating whether the dialog should be hidden.
 * @param {string} [cta='Ok'] - The text for the confirm button.
 * @param {string} [cancel='Cancel'] - The text for the cancel button.
 * @param {boolean} [canConfirm=true] - A boolean indicating whether the confirm button should be enabled.
 *
 * @returns {JSX.Element | null} The rendered dialog component or null if shouldHide is true.
 */
export function ConfirmDialog({
  title,
  open = true,
  onClose,
  onConfirm,
  cta = 'Confirm',
  cancel = 'Cancel',
  canConfirm = true,
  destructive = false,
  children,
}: ConfirmDialogProps) {
  if (!open) return null

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <OutlinedButton
          data-testid="dialog-cancel"
          size="small"
          onClick={onClose}
        >
          {cancel}
        </OutlinedButton>
        <FilledInButton
          color={destructive ? 'error' : 'primary'}
          data-testid="dialog-confirm"
          size="small"
          disabled={!canConfirm}
          onClick={onConfirm}
        >
          {cta}
        </FilledInButton>
      </DialogActions>
    </Dialog>
  )
}
